"use client";

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/utils/supabase/client';
import { showSuccess, showError } from '@/utils/toast';
import { Mail } from 'lucide-react';

interface EmailSenderProps {
  to: string;
  subject: string;
  template: 'invoice' | 'quote' | 'general';
  invoiceId?: string;
  quoteId?: string;
}

export const EmailSender = ({ to, subject, template, invoiceId, quoteId }: EmailSenderProps) => {
  const [isSending, setIsSending] = useState(false);

  const sendEmail = async () => {
    if (!to) {
      showError('Recipient email is required');
      return;
    }

    setIsSending(true);
    try {
      const body = {
        to,
        subject,
        template,
        ...(invoiceId && { invoice_id: invoiceId }),
        ...(quoteId && { quote_id: quoteId }),
        from_name: 'Your Company',
        message: `Please review the attached ${template}. If you have questions, reply to this email.`,
      };

      const { data, error } = await supabase.functions.invoke('send-email', { body });
      if (error) throw error;

      showSuccess(`Email sent to ${to}!`);
    } catch (error) {
      console.error('Email send error:', error);
      showError('Failed to send email. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={sendEmail}
      disabled={isSending || !to}
    >
      {isSending ? (
        <>
          <Mail className="h-4 w-4 mr-2 animate-spin" />
          Sending...
        </>
      ) : (
        <>
          <Mail className="h-4 w-4 mr-2" />
          Send Email
        </>
      )}
    </Button>
  );
};